import React, { useEffect, useMemo, useState } from 'react';
import { Search, Users } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { mockData } from '../../../services/mockData';
import { PracticeAttendee } from '../../../types/practice';
import { workerToAttendee } from '../../../utils/practices';
interface AttendeesPickerModalProps {
  isOpen: boolean;
  companyName: string;
  selected: PracticeAttendee[];
  onClose: () => void;
  onConfirm: (attendees: PracticeAttendee[]) => void;
}

// Check-list of the company's Risorse umane, to choose who attends the course
export function AttendeesPickerModal({
  isOpen,
  companyName,
  selected,
  onClose,
  onConfirm
}: AttendeesPickerModalProps) {
  const workers = useMemo(() => {
    const company = mockData.getClientByName(companyName);
    return company ? mockData.getWorkersByClient(company.id) : [];
  }, [companyName]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setChecked(new Set(selected.map(a => a.workerId).filter((id): id is number => id !== undefined)));
  }, [isOpen, selected]);
  const q = query.trim().toLowerCase();
  const visible = workers.filter(w => !q || w.name.toLowerCase().includes(q) || w.role.toLowerCase().includes(q) || w.department.toLowerCase().includes(q));
  const allVisibleChecked = visible.length > 0 && visible.every(w => checked.has(w.id));
  const toggle = (id: number) => setChecked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setChecked(prev => {
    const next = new Set(prev);
    visible.forEach(w => allVisibleChecked ? next.delete(w.id) : next.add(w.id));
    return next;
  });
  const confirm = () => onConfirm(workers.filter(w => checked.has(w.id)).map(workerToAttendee));
  return <Modal isOpen={isOpen} onClose={onClose} title="Partecipanti al corso" size="xl" data-id="element-1458" data-mp="x7zdyumfz5pu">
      <div className="space-y-4" data-id="element-1459" data-mp="ib09vgqstv04">
        <p className="text-sm text-slate-500 -mt-1" data-id="element-1460" data-mp="ibrtdt74g2a1">
          Seleziona le Risorse umane di <strong className="text-slate-700" data-id="element-1461" data-mp="n9jwzkh4pcu2">{companyName}</strong> che
          parteciperanno al corso.
        </p>

        {workers.length === 0 ? <div className="text-center py-8" data-id="element-1462" data-mp="9hdi01pppbw7">
            <Users className="h-10 w-10 text-slate-300 mx-auto" data-id="element-1463" data-mp="e9wuswgks2zy" />
            <p className="text-sm text-slate-500 mt-2" data-id="element-1464" data-mp="eb7xsu2756q0">
              Nessuna risorsa umana in anagrafica per questa azienda.
            </p>
          </div> : <>
            <div className="relative" data-id="element-1465" data-mp="4jv5608kqcix">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1466" data-mp="3mpggnaex2j4z" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per nome, mansione o reparto..." aria-label="Cerca risorse umane" className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 pl-9 pr-4 py-2 text-sm focus:border-seform-orange focus:bg-white focus:outline-none" data-id="element-1467" data-mp="3qqjwhwwysf67" />
            </div>
            <div className="flex items-center justify-between text-sm" data-id="element-1468" data-mp="3c67haevy0og1">
              <label className="inline-flex items-center gap-2 cursor-pointer font-medium text-slate-700" data-id="element-1469" data-mp="3r02jj21lffi9">
                <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={allVisibleChecked} onChange={toggleAll} disabled={visible.length === 0} data-id="element-1470" data-mp="3m1r9aq7dz2zi" />
                Seleziona tutti
              </label>
              <span className="text-slate-500" data-id="element-1471" data-mp="8nw9pqfmps6z">
                {checked.size} di {workers.length} selezionati
              </span>
            </div>
            <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto" data-id="element-1472" data-mp="sp8r25klav5u">
              {visible.map(w => <li key={w.id} data-id="element-1473" data-mp="11hizpifu6skz">
                  <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50" data-id="element-1474" data-mp="11icacuhx228e">
                    <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={checked.has(w.id)} onChange={() => toggle(w.id)} data-id="element-1475" data-mp="16g40pk2wxgah" />
                    <span className="flex-1 min-w-0" data-id="element-1476" data-mp="ipqa7xzz7pi0">
                      <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-1477" data-mp="nqxgttqduin3">
                        {w.name}
                      </span>
                      <span className="block text-xs text-slate-500 truncate" data-id="element-1478" data-mp="1u681zksmp3yb">
                        {w.role} · {w.department}
                      </span>
                    </span>
                    <span className="text-xs text-slate-400 truncate hidden sm:block" data-id="element-1479" data-mp="3jn2bdpzr2ry6">
                      {w.email}
                    </span>
                  </label>
                </li>)}
              {visible.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-500" data-id="element-1480" data-mp="34yz2a7qev055">
                  Nessun risultato per "{query}"
                </li>}
            </ul>
          </>}

        <div className="flex justify-end gap-3 pt-2" data-id="element-1481" data-mp="2vyao9d10ozs9">
          <Button variant="flat" color="default" onClick={onClose} data-id="element-1482" data-mp="2vwptbmasjetv">
            Annulla
          </Button>
          <Button onClick={confirm} disabled={workers.length === 0} data-id="element-1483" data-mp="2r10r1b7dzjtv">
            Conferma partecipanti ({checked.size})
          </Button>
        </div>
      </div>
    </Modal>;
}